import { useCallback, useState } from 'react';
import { useStore } from '../store';
import { PermissionSlug } from '../constants/featureFlags';
import { useToast } from './useToastContext';
import { useShpOasisSubmissionEnabled } from './useShpOasisSubmissionEnabled';
import { SHP_UNSENDABLE_REASONS } from '../components/oasis/cmsFieldErrors';
import { getBusinessErrorMessage } from '../utils/apiErrorDetail';
import { getHttpStatus } from '../utils/httpErrorUtils';
import { runOasisShpCheckVisitsOasisManagerVisitIdShpCheckPost } from '../services/api/sdk.gen';
import type {
  OasisExportReadiness,
  ShpLastCheckResponse,
  ShpUnsendableReason,
} from '../services/api/types.gen';

export const SHP_CHECK_NOT_REACHED =
  'The SHP check did not reach SHP. Try again.';
export const SHP_CHECK_POPUP_BLOCKED =
  'Allow pop-ups for Roger to see the SHP alerts.';
export const SHP_CHECK_CMS_CHECK_FAILED =
  'The CMS check of this chart did not finish, so nothing went to SHP. Try again.';

/** Whether this user may run an SHP check here: the agency sends to SHP and
 * the user holds the permission. The server checks both again. */
export function useCanRunShpCheck(): boolean {
  const shpEnabled = useShpOasisSubmissionEnabled();
  const allowed = useStore(s =>
    s.hasPermission(PermissionSlug.OASIS_SEND_SHP_CHECK)
  );
  return shpEnabled && allowed;
}

/** An https link on SHP's own site; the server checks the same. */
export function isShpAlertUrl(value: string): boolean {
  try {
    const url = new URL(value);
    const host = url.hostname.toLowerCase();
    return (
      url.protocol === 'https:' &&
      (host === 'shpdata.com' || host.endsWith('.shpdata.com'))
    );
  } catch {
    return false;
  }
}

function failureMessage(error: unknown): string {
  const status = getHttpStatus(error);
  if (status === undefined || status >= 500) return SHP_CHECK_NOT_REACHED;
  const code = (error as { response?: { data?: { code?: unknown } } })?.response
    ?.data?.code;
  if (typeof code === 'string' && code in SHP_UNSENDABLE_REASONS) {
    return `SHP check not sent: ${SHP_UNSENDABLE_REASONS[code as ShpUnsendableReason]}.`;
  }
  return getBusinessErrorMessage(error) ?? 'The SHP check could not run.';
}

interface UseShpCheckOptions {
  /** CMS errors kept the chart back; nothing went to SHP. */
  onCmsErrors?: (visitId: string, readiness: OasisExportReadiness) => void;
  /** SHP answered; the check is recorded. */
  onChecked?: (visitId: string, lastCheck: ShpLastCheckResponse | null) => void;
}

/**
 * Sends one chart to SHP and opens SHP's alerts in a new window.
 *
 * SHP's page refuses to load inside another site, so it gets a window of its
 * own. The window is opened on the click, before the call: a pop-up blocker
 * lets through only a window the click itself opens.
 */
export function useShpCheck({ onCmsErrors, onChecked }: UseShpCheckOptions) {
  const toast = useToast();
  const addToast = useStore(s => s.addToast);
  const [checkingId, setCheckingId] = useState<string | null>(null);

  const run = useCallback(
    async (visitId: string) => {
      const win = window.open('', '_blank');
      try {
        win?.document.write(
          '<p style="font-family:sans-serif">Checking with SHP…</p>'
        );
      } catch {
        // A window the browser will not let us write to still works.
      }
      setCheckingId(visitId);
      try {
        const { data } =
          await runOasisShpCheckVisitsOasisManagerVisitIdShpCheckPost({
            path: { visit_id: visitId },
            throwOnError: true,
          });
        const link = data.alert_url;
        if (link && isShpAlertUrl(link)) {
          onChecked?.(visitId, data.last_check ?? null);
          if (win) {
            win.opener = null;
            win.location.href = link;
          } else {
            addToast({
              title: SHP_CHECK_POPUP_BLOCKED,
              variant: 'destructive',
              duration: null,
              linkAction: {
                label: 'Open SHP alerts',
                altText: 'Open the SHP alerts in a new tab',
                href: link,
                newTab: true,
              },
            });
          }
          return;
        }
        win?.close();
        // Only a blocked chart has errors to show; a failed scrub has none.
        if (data.readiness?.status === 'blocked') {
          onCmsErrors?.(visitId, data.readiness);
          return;
        }
        if (data.readiness) {
          toast.error(SHP_CHECK_CMS_CHECK_FAILED);
          return;
        }
        toast.error(SHP_CHECK_NOT_REACHED);
      } catch (error) {
        win?.close();
        console.error('SHP check failed', error);
        toast.error(failureMessage(error));
      } finally {
        setCheckingId(null);
      }
    },
    [addToast, onChecked, onCmsErrors, toast]
  );

  return { run, checkingId };
}
